import { BarChart, type BarLabelProps } from '@mui/x-charts/BarChart';
import {
  StyledTotals,
  StyledTotal,
  StyledBarLabel,
  StyledIcon,
  StyledStatistic,
  StyledTitle,
} from './ROIChart.styles';

export type ROIChartArgs = {
  xAxisLabel: string;
  timePeriods: number;
  toolTip: string;
  values: number[];
  hoursSaved: number;
  hoursSavedLabel: string;
  costSaved: number;
  costSavedLabel: string;
  perTimePeriodLabel: string;
  currency: Queries.kontent_item_roi_calculator['elements']['currency']['value'];
  hoursSavedIcon?: Queries.ROI_CALCULATORFragment['elements']['hours_saved_icon'];
  costSavedIcon?: Queries.ROI_CALCULATORFragment['elements']['cost_saved_icon'];
  title?: string | null;
};

const formatCurrency = (
  value: number | null | undefined,
  currency: Queries.kontent_item_roi_calculator['elements']['currency']['value']
) => {
  return (value ?? 0).toLocaleString(undefined, {
    style: 'currency',
    currency: currency ?? 'usd',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const ROIChart = ({
  xAxisLabel,
  timePeriods,
  values,
  toolTip,
  hoursSaved,
  hoursSavedLabel,
  costSaved,
  costSavedLabel,
  perTimePeriodLabel,
  currency,
  hoursSavedIcon,
  costSavedIcon,
  title,
}: ROIChartArgs) => {
  // Early return for invalid data
  if (!values?.length || timePeriods <= 0) {
    return <div>No data available</div>;
  }

  const hasHoursSavedIcon = Boolean(hoursSavedIcon?.value?.url);
  const hasCostSavedIcon = Boolean(costSavedIcon?.value?.url);

  return (
    <>
      <div>
        {title && <StyledTitle>{title}</StyledTitle>}
        <BarChart
          hideLegend
          xAxis={[
            {
              id: 'barCategories',
              data: Array.from({ length: timePeriods }, (_, i) => `${xAxisLabel} ${i + 1}`),
              scaleType: 'band',
              hideTooltip: true,
              tickLabelStyle: {
                fontSize: 12,
                fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
                fontWeight: 'bold',
                fill: 'rgba(0, 0, 0, 0.7)',
              },
            },
          ]}
          yAxis={[
            {
              position: 'none',
              hideTooltip: true,
              disableTicks: true,
              disableLine: true,
              max: Math.max(...values) * 1.1,
            },
          ]}
          series={[
            {
              data: values,
              color: 'rgba(18, 110, 211, 0.8)',
              valueFormatter: /* istanbul ignore next */ (value) => formatCurrency(value, currency),
              label: toolTip,
            },
          ]}
          barLabel={(item) => {
            return formatCurrency(item.value, currency);
          }}
          slots={{ barLabel }}
          borderRadius={6}
          height={350}
          grid={{ horizontal: true, vertical: false }}
        />
      </div>
      <StyledTotals>
        <StyledStatistic>
          {hasHoursSavedIcon && <StyledIcon {...hoursSavedIcon!.value!} />}
          <div>
            <StyledTotal>{(hoursSaved ?? 0).toLocaleString()}</StyledTotal>
            <p>{hoursSavedLabel}</p>
            <p>{perTimePeriodLabel}</p>
          </div>
        </StyledStatistic>
        <StyledStatistic>
          {hasCostSavedIcon && <StyledIcon {...costSavedIcon!.value!} />}
          <div>
            <StyledTotal>{formatCurrency(costSaved, currency)}</StyledTotal>
            <p>{costSavedLabel}</p>
            <p>{perTimePeriodLabel}</p>
          </div>
        </StyledStatistic>
      </StyledTotals>
    </>
  );
};

export default ROIChart;

function barLabel(props: BarLabelProps) {
  const { x, y, width } = props;
  const customProps = { ...props, y: y - 10, x: x + width / 2, textAnchor: 'middle' };
  return <StyledBarLabel {...customProps} />;
}
